import { Stack, Group, MultiSelect, TextInput, NumberInput, Checkbox } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconSearch, IconCalendar } from '@tabler/icons-react';
import { useCategories } from '@/hooks/api/useCategories';
import { useDepartments } from '@/hooks/api/useDepartments';
import { useLocations } from '@/hooks/api/useLocations';
import { useOrganization } from '@/hooks/useOrganization';

export interface AssetFilter {
  status?: string[];
  categories?: string[];
  departments?: string[];
  locations?: string[];
  dateRange?: {
    start: string | null;
    end: string | null;
  };
  costRange?: {
    min: number | null;
    max: number | null;
  };
  searchTerm?: string;
  isDepreciable?: boolean;
}

interface AssetFilterContentProps {
  filters: AssetFilter;
  onFiltersChange: (filters: AssetFilter) => void;
}

export function AssetFilterContent({ filters, onFiltersChange }: AssetFilterContentProps) {
  const { organizationId } = useOrganization();

  const { data: categories = [] } = useCategories(organizationId);
  const { data: departments = [] } = useDepartments(organizationId);
  const { data: locations = [] } = useLocations(organizationId);

  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'Maintenance', label: 'Maintenance' },
    { value: 'Retired', label: 'Retired' },
    { value: 'Lost', label: 'Lost' },
  ];

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const departmentOptions = departments.map((dept) => ({
    value: dept.id,
    label: dept.name,
  }));

  const locationOptions = locations.map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search by description, tag, brand, model, or serial number"
        leftSection={<IconSearch size={16} />}
        value={filters.searchTerm || ''}
        onChange={(e) =>
          onFiltersChange({
            ...filters,
            searchTerm: e.target.value || undefined,
          })
        }
      />

      <Group grow>
        <MultiSelect
          label="Status"
          placeholder="All statuses"
          data={statusOptions}
          value={filters.status || []}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              status: value.length > 0 ? value : undefined,
            })
          }
          clearable
        />

        <MultiSelect
          label="Category"
          placeholder="All categories"
          data={categoryOptions}
          value={filters.categories || []}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              categories: value.length > 0 ? value : undefined,
            })
          }
          clearable
        />
      </Group>

      <Group grow>
        <MultiSelect
          label="Department"
          placeholder="All departments"
          data={departmentOptions}
          value={filters.departments || []}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              departments: value.length > 0 ? value : undefined,
            })
          }
          clearable
        />

        <MultiSelect
          label="Location"
          placeholder="All locations"
          data={locationOptions}
          value={filters.locations || []}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              locations: value.length > 0 ? value : undefined,
            })
          }
          clearable
        />
      </Group>

      <Group grow>
        <DateInput
          label="Purchase Date From"
          placeholder="Start date"
          leftSection={<IconCalendar size={16} />}
          valueFormat="YYYY-MM-DD"
          value={filters.dateRange?.start ? new Date(filters.dateRange.start) : null}
          onChange={(date) =>
            onFiltersChange({
              ...filters,
              dateRange: {
                start: date?.toISOString().split('T')[0] || null,
                end: filters.dateRange?.end || null,
              },
            })
          }
          clearable
        />

        <DateInput
          label="Purchase Date To"
          placeholder="End date"
          leftSection={<IconCalendar size={16} />}
          valueFormat="YYYY-MM-DD"
          value={filters.dateRange?.end ? new Date(filters.dateRange.end) : null}
          onChange={(date) =>
            onFiltersChange({
              ...filters,
              dateRange: {
                start: filters.dateRange?.start || null,
                end: date?.toISOString().split('T')[0] || null,
              },
            })
          }
          clearable
        />
      </Group>

      <Group grow>
        <NumberInput
          label="Min Cost"
          placeholder="0.00"
          prefix="$"
          decimalScale={2}
          value={filters.costRange?.min || ''}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              costRange: {
                min: typeof value === 'number' ? value : null,
                max: filters.costRange?.max || null,
              },
            })
          }
        />

        <NumberInput
          label="Max Cost"
          placeholder="0.00"
          prefix="$"
          decimalScale={2}
          value={filters.costRange?.max || ''}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              costRange: {
                min: filters.costRange?.min || null,
                max: typeof value === 'number' ? value : null,
              },
            })
          }
        />
      </Group>

      <Checkbox
        label="Show only depreciable assets"
        checked={filters.isDepreciable === true}
        onChange={(e) =>
          onFiltersChange({
            ...filters,
            isDepreciable: e.target.checked ? true : undefined,
          })
        }
      />
    </Stack>
  );
}
